import React, {useState, useEffect, useMemo} from 'react';
import {useDropzone} from 'react-dropzone';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Box, Divider, Stack} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Icon,
  Label,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProcessEvent from '@contentlab/hooks/analytics/use-process-event';
import translate from '@/utils/translate';
import {checkIncomingAssets} from '@/views/content-studio/assets/utils';
import useAssetsQuery from '@/hooks/content-studio/use-assets-query';
import useShouldPollForUploads from '@/hooks/content-studio/use-should-poll-for-uploads';
import contentStudioStore, {
  contentStudioActions,
} from '@/stores/contentStudioStore';
import UploadDisplay from './UploadDisplay';
import {onFileInput} from './utils';

type FileUploaderProps = {
  height?: string | number;
  width?: string | number;
  maxWidth?: string | number;
  analyticsLocation: string;
};

function FileUploader({
  height,
  width,
  maxWidth,
  analyticsLocation,
}: FileUploaderProps) {
  const {selectedAssets, files} = contentStudioStore(
    useShallow((state) => ({
      selectedAssets: state.selectedAssets,
      files: state.files,
    }))
  );
  const {uiInteraction} = useUiInteractionEvent();
  const processEventHandlers = useProcessEvent();
  const [uploadInteractionId, setUploadInteractionId] = useState(uuid());

  // Poll while at least one selected asset is still uploading and its local upload reached 100 %
  const shouldPollForUploads = useShouldPollForUploads(true);

  const {data: uploadsData} = useAssetsQuery(
    {field: 'created_at', desc: true},
    {type: 'uploads'},
    shouldPollForUploads
  );

  // Sync selectedAssets with backend status once polling returns READY assets
  useEffect(() => {
    if (!uploadsData) return;

    const checkedUploadsData = checkIncomingAssets(
      uploadsData.pages || [],
      selectedAssets,
      files
    );

    checkedUploadsData.assetsToUpdate.forEach((asset) => {
      const sel = selectedAssets.find((s) => s.id === asset.id);
      if (sel && sel.isUploading) {
        contentStudioActions.updateSelectedAsset({
          id: asset.id,
          isUploading: false,
          thumbnail: asset.thumbnailUrl,
          type: 'Uploads',
          fileName: asset.name,
          url: asset.url,
          durationMillis: asset.metadata.durationMillis,
          aspectRatio: asset.metadata.aspectRatio,
        });
      }
    });

    checkedUploadsData.filesToRemove.forEach((fileName) => {
      contentStudioActions.removeFile(fileName);
    });
  }, [uploadsData, selectedAssets, files]);

  const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
    onDrop: (acceptedFiles) => {
      onFileInput(
        acceptedFiles,
        uploadInteractionId,
        analyticsLocation,
        processEventHandlers
      );
      setUploadInteractionId(uuid());
    },
    accept: {
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'video/x-msvideo': ['.avi'],
      'video/webm': ['.webm'],
    },
  });

  return (
    <Box
      bgcolor={designSystemToken('semantic.bg.tertiary')}
      minHeight={height}
      width={width}
      maxWidth={maxWidth}
      sx={{
        cursor: 'pointer',
        display: 'grid',
        placeItems: 'center',
        borderRadius: '8px',
      }}
    >
      <div
        {...getRootProps({
          onClick: () => {
            uiInteraction({
              item_name: 'browse_files',
              interaction_location: analyticsLocation,
              interaction_id: uploadInteractionId,
            });
          },
        })}
        className={`dropzone ${isDragActive ? 'active' : ''}`}
        style={{
          display: 'flex',
          flexFlow: 'row nowrap',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <input {...getInputProps()} />
        {selectedAssets.length === 0 ? (
          <Stack
            direction="column"
            margin="0 auto"
            gap="8px"
            alignItems="center"
          >
            <Icon
              name="Actions-Upload"
              size="medium"
              appearance="primary"
              style={{alignSelf: 'center'}}
            />
            <Stack direction="row" alignItems="center">
              <Label size="lg">
                {translate('Drag & drop your video files here')}
              </Label>
            </Stack>
            <Divider flexItem>
              <Body
                size="sm"
                color={designSystemToken('semantic.fg.inverse-secondary')}
              >
                {translate('or')}
              </Body>
            </Divider>
            <Button
              appearance="neutral"
              mode="tinted"
              size="large"
              sx={{borderRadius: '8px'}}
            >
              {translate('Browse your computer')}
            </Button>
            <Body
              size="lg"
              color={designSystemToken('semantic.fg.secondary')}
              sx={{mt: '6px', mb: '4px'}}
            >
              {translate(
                'Videos up to 3 min and 1GB each (mp4, mov, webm, avi)'
              )}
            </Body>
          </Stack>
        ) : (
          <UploadDisplay
            analyticsLocation={analyticsLocation}
            open={open}
            maxWidth={parseInt(maxWidth as string, 10)}
            uploadInteractionId={uploadInteractionId}
          />
        )}
      </div>
    </Box>
  );
}

export default FileUploader;
